import React, { useCallback, useContext } from 'react'
import _ from 'lodash'
import { useDispatch } from 'react-redux'
import { v4 } from 'uuid'
import classNames from 'classnames'
import { useForm, Controller } from 'react-hook-form'

import { deleteAction, addAction, updateAction } from 'Actions/flow'
import { StepContext } from 'Src/containers/contexts'
import ToggleSwitch from '../../../../components/Toggle'

const ACTION_TYPE = 'happyfox_workflow_action'

const HappyFoxWorkflowEdit = (props) => {
  const { mode = 'edit', actionId, cancelFn = _.noop, disableOptions = false } = props

  const dispatch = useDispatch()
  const { stepId, step } = useContext(StepContext)

  const settings = _.get(step, 'settings', {})
  const action = _.get(settings, `${actionId}`, {
    type: ACTION_TYPE
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    control
  } = useForm({
    defaultValues: {
      url: action.workflowUrl,
      waitForResponse: action.waitForResponse ?? false
    }
  })

  const canShowDeleteBtn = mode === 'edit'

  const editAction = useCallback(
    (data) => {
      dispatch(
        updateAction({
          stepId,
          actionId,
          setting: {
            type: ACTION_TYPE,
            workflowUrl: data.url,
            waitForResponse: data.waitForResponse
          }
        })
      )
      cancelFn()
    },
    [dispatch, cancelFn, stepId, actionId]
  )

  const createAction = useCallback(
    (data) => {
      dispatch(
        addAction({
          stepId,
          actionId: v4(),
          setting: {
            type: ACTION_TYPE,
            workflowUrl: data.url,
            waitForResponse: data.waitForResponse
          }
        })
      )
    },
    [dispatch, stepId]
  )

  const onSaveClick = useCallback(
    (submitData) => {
      const data = {
        url: submitData.url.trim(),
        waitForResponse: submitData.waitForResponse
      }

      if (mode === 'create') {
        createAction(data)
      } else {
        editAction(data)
      }

      cancelFn()
    },
    [cancelFn, mode, createAction, editAction]
  )

  const onDeleteClick = useCallback(() => {
    dispatch(
      deleteAction({
        stepId,
        actionId
      })
    )
  }, [dispatch, actionId, stepId])

  return (
    <form onSubmit={handleSubmit(onSaveClick)}>
      <div className='form-group'>
        <div className='label mb-2'>Enter Workflow URL</div>
        <input
          type='text'
          className={classNames('form-control', { 'is-invalid': errors.url })}
          placeholder='Enter a workflow URL...'
          {...register('url', {
            required: {
              value: true,
              message: 'Workflow URL is required'
            },
            pattern: {
              value:
                /https:\/\/([A-Za-z][A-Za-z0-9_-]+?).happyfox.com?\/workflows\/([0-9]+)\/webhooks\/([A-Za-z0-9_-]+?)\/$/,
              message: 'Enter a valid workflow URL'
            }
          })}
        />
        <div className='invalid-feedback'>{errors.url?.message}</div>
      </div>
      <div className='form-group d-flex'>
        <div className='label'>Wait for response from Workflows</div>
        <Controller
          name='waitForResponse'
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <ToggleSwitch
                className='ml-auto'
                checked={value}
                onClick={() => {
                  onChange(!value)
                }}
              />
            )
          }}
        />
      </div>
      {!disableOptions && (
        <div className='options'>
          <button className='button-primary small save' type='submit'>
            Save
          </button>
          <button type='button' className='btn cancel' onClick={cancelFn}>
            Cancel
          </button>
          {canShowDeleteBtn && (
            <button type='button' className='btn delete' onClick={onDeleteClick}>
              Delete
            </button>
          )}
        </div>
      )}
    </form>
  )
}

export default HappyFoxWorkflowEdit
